'use client';
import type { StatuslineConfig } from '../../lib/types';
import { useBrainChat, useBrainChatStatus } from './BrainChatProvider';

/** Whether the statusline row has any statistic to show at all. The daemon reports `statusline: null` when
 *  the plugin is disabled, so `lineCfg` IS the plugin set as a surface sees it — no second source. It is
 *  `undefined` until the first status read lands, which shows no row either. */
export function statuslineHasStats(lineCfg: StatuslineConfig | null | undefined): boolean {
  return !!lineCfg && !!(lineCfg.showModel || lineCfg.showContext || lineCfg.showTokens || lineCfg.showSpeed || lineCfg.showCacheHit || lineCfg.showCost);
}

/** Which of the model control's two possible hosts renders it: the statusline under the conversation, or
 *  the conversation bar (inline, or in its ⋯ menu). */
type ModelControlHost = 'statusline' | 'bar';

/** The ONE answer to "who owns the model control", read by both of the control's possible hosts.
 *
 *  It is not the plugin's presence alone. The statusline can be enabled while its model toggle is off, and
 *  the reader can collapse the whole row from its chevron; in either state the pill is not on screen, and
 *  a top bar that had already given up its picker would leave no way to change models at all. So the bar
 *  stands down exactly when the statusline is actually rendering the control, and takes it back otherwise.
 *
 *  `undefined` until the first status read has delivered the plugin's config: neither host renders the
 *  control then. The bar used to read that gap as "plugin disabled" and showed its picker for a moment after
 *  every reload, only to drop it when the statusline took over. A briefly absent control is calmer than one
 *  that appears and vanishes, and reserving its slot would leave a gap in the common case where the bar
 *  ends up without it.
 *
 *  `currentModel` is part of that condition and not an extra: a conversation the controller has not
 *  adopted — a history entry being previewed — has no model to switch, so the statusline prints the name
 *  as a read-only span (see the model slot in the dock) rather than the picker. Reading the plugin's
 *  toggles alone left that state with no switcher anywhere: not in the bar's wide controls, not in the
 *  compact dock, not in the phone's ⋯ menu. A reload does not pass through that state: the first status
 *  read delivers the config and the adopted conversation's model in one update. */
export function useModelControlHost(statuslineShown: boolean): ModelControlHost | undefined {
  const { currentModel } = useBrainChat();
  const { lineCfg } = useBrainChatStatus();
  if (lineCfg === undefined) return undefined;
  return lineCfg?.showModel && statuslineHasStats(lineCfg) && statuslineShown && currentModel ? 'statusline' : 'bar';
}
